import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import { Vendor } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class VendorService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService,
    private UtilityService: UtilityService
  ) {}

  public convertVendorToComplianceExempt(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/convertVendorToComplianceExempt`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(
              `/api/v2/admin/vendor/convertVendorToComplianceExempt`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getVendorV2(VendorId: number, query = {}): Observable<Vendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get<Vendor>(`/api/v2/vendor/${VendorId}/one`, {
              params: query
            }),
          Client: () =>
            this.http.get<Vendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${VendorId}/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<Vendor>(`/api/v2/vendor/${VendorId}/one`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getVendors(query = {}): Observable<Vendor[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<Vendor[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<Vendor[]>(`/api/v2/admin/vendor/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getVendorCount(query = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/vendor/count`, {
              params: query
            }),
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/count`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
    // return this.http.get(`/api/v2/vendor/${VendorId}/getVendorCount`, { params: query });
  }

  public getMaskedVendorSSN(query = {}) {
    return this.http.get<Vendor>(`/api/v2/admin/vendor/maskedSSN`, {
      params: query
    });
  }

  public getFullVendorSSN(query = {}) {
    return this.http.get<Vendor>(`/api/v2/admin/vendor/fullSSN`, {
      params: query
    });
  }

  // claimCode is an optional param.
  public clientSignup(VendorId: number, vendorSignUpCode: string, query = {}) {
    return this.http.post(
      `/api/v2/vendor/${VendorId}/clientSignup/${vendorSignUpCode}`,
      {},
      { params: query }
    );
  }

  public saveVendor(Vendor: Vendor, query = {}): Observable<any> {
    return this.http.post(`/api/v2/vendor/${Vendor.id}/saveVendor`, Vendor, {
      params: query
    });
  }

  public getVendorLogoS3PresignedUrl(
    VendorId: number
  ): Observable<S3PresignedUrl> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Vendor: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/vendor/${VendorId}/${VendorId}/getVendorLogoS3PresignedUrl`
            ),
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/vendor/${VendorId}/getVendorLogoS3PresignedUrl`
            ),
          Admin: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/admin/vendor/${VendorId}/${VendorId}/getVendorLogoS3PresignedUrl`
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
